"use client"

// P-3 (audit 2026-05-28): moved from components/admin/InlineToggle.tsx
// into the DS so non-admin surfaces (POS, kiosk, employee) can use it
// without reaching into the admin namespace. API and behavior are
// preserved exactly so existing callers can switch the import path
// with zero JSX changes.

import { useState, useEffect } from "react"

export interface ToggleProps {
  value: boolean
  /**
   * Returns a Promise so the toggle can render an optimistic state +
   * rollback if the persist call fails. If the caller is synchronous,
   * return Promise.resolve().
   */
  onToggle: (next: boolean) => Promise<void>
  disabled?: boolean
  /**
   * ARIA label for screen readers. Required because the toggle is a
   * pure visual — without a label, VO/JAWS read it as "switch toggle".
   */
  "aria-label"?: string
}

export function Toggle({ value, onToggle, disabled, ...rest }: ToggleProps) {
  const [optimistic, setOptimistic] = useState(value)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!loading) setOptimistic(value)
  }, [value, loading])

  async function handleClick() {
    if (disabled || loading) return
    const next = !optimistic
    setOptimistic(next)
    setLoading(true)
    try {
      await onToggle(next)
    } catch {
      setOptimistic(!next)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled || loading}
      role="switch"
      aria-checked={optimistic}
      aria-label={rest["aria-label"]}
      className="relative inline-flex h-6 w-10 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--redy-red)] disabled:opacity-50 disabled:cursor-not-allowed"
      style={optimistic ? { background: "var(--redy-gradient)" } : { background: "var(--redy-border)" }}
    >
      <span
        className={[
          "inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200",
          optimistic ? "translate-x-5" : "translate-x-1",
        ].join(" ")}
        aria-hidden
      />
    </button>
  )
}
